import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone, Instagram, Mail, CircleUser } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/api";

const NAV = [
  ["Home", "/"], ["Services", "/services"], ["Pricing", "/pricing"], ["Offers", "/offers"],
  ["Service Areas", "/service-areas"], ["About", "/about"], ["Contact", "/contact"],
];

export function Header() {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const accountTo = user ? (user.role === "admin" ? "/admin" : "/account") : "/login";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[var(--kc-bg)] border-b border-[#E5E2DA]">
      <div className="kc-container flex h-16 sm:h-20 items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
          {NAV.map(([label, to]) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `px-3 py-2 rounded-full text-sm font-medium transition-colors duration-150 ${isActive ? "text-[var(--kc-blue)] bg-white" : "text-[#5A606A] hover:text-[var(--kc-text)] hover:bg-white/70"}`}>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {settings?.phone && (
            <a href={`tel:${settings.phone}`} data-testid="header-phone-link" className="btn-ghost hidden md:inline-flex text-sm">
              <Phone className="w-4 h-4" /> {settings.phone}
            </a>
          )}
          <Link to={accountTo} data-testid="header-account-link" className="btn-ghost h-10 w-10 px-0" aria-label="Account">
            <CircleUser className="w-5 h-5" />
          </Link>
          <button data-testid="header-book-now-button" onClick={() => { track("homepage_book_clicked", { source: "header" }); navigate("/book"); }}
            className="btn-primary h-10 px-3 sm:px-5 text-xs sm:text-sm whitespace-nowrap">BOOK NOW</button>
          <button className="lg:hidden btn-ghost h-10 w-10 px-0" aria-label="Menu" data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#E5E2DA] bg-[var(--kc-bg)]" data-testid="mobile-menu">
          <nav className="kc-container py-4 grid gap-1">
            {NAV.map(([label, to]) => (
              <NavLink key={to} to={to} end={to === "/"} onClick={() => setOpen(false)}
                className={({ isActive }) => `px-3 py-3 rounded-xl text-base font-medium ${isActive ? "bg-white text-[var(--kc-blue)]" : "text-[var(--kc-text)]"}`}>{label}</NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { settings } = useSettings();
  return (
    <footer className="bg-[var(--kc-text)] text-white mt-auto">
      <div className="kc-container py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <Logo light />
          <p className="mt-5 text-white/70 max-w-sm leading-relaxed">{settings?.brand_theme}</p>
          <p className="mt-3 text-sm text-[#FBBF24] font-medium" data-testid="footer-region">{settings?.service_region_label}</p>
        </div>
        <div>
          <h4 className="text-sm font-semibold tracking-wider uppercase text-white/60 mb-4">Explore</h4>
          <ul className="space-y-2 text-white/85">
            {NAV.slice(1).map(([l, to]) => <li key={to}><Link to={to} className="hover:text-[#FBBF24] transition-colors">{l}</Link></li>)}
            <li><Link to="/faq" className="hover:text-[#FBBF24] transition-colors">FAQ</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-semibold tracking-wider uppercase text-white/60 mb-4">Contact</h4>
          <ul className="space-y-3 text-white/85">
            {settings?.phone && <li><a href={`tel:${settings.phone}`} data-testid="footer-phone" className="inline-flex items-center gap-2 hover:text-[#FBBF24]"><Phone className="w-4 h-4" />{settings.phone}</a></li>}
            {settings?.email && <li><a href={`mailto:${settings.email}`} data-testid="footer-email" className="inline-flex items-start gap-2 hover:text-[#FBBF24] break-all"><Mail className="w-4 h-4 mt-0.5 shrink-0" />{settings.email}</a></li>}
            {settings?.instagram && <li><a href={settings.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="inline-flex items-center gap-2 hover:text-[#FBBF24]"><Instagram className="w-4 h-4" />Instagram</a></li>}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="kc-container py-5 text-xs text-white/50 flex flex-col sm:flex-row justify-between gap-2">
          <span>© {new Date().getFullYear()} {settings?.business_name}. All prices in {settings?.currency || "CAD"}.</span>
          <span>Professional Cleaning · Reliable Service · Affordable Prices</span>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  const { pathname } = useLocation();
  const hideSticky = ["/login", "/signup", "/forgot-password", "/reset-password", "/book"].some((p) => pathname.startsWith(p)) || pathname.startsWith("/account");
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className={`flex-1 ${hideSticky ? "" : "pb-20 md:pb-0"}`}><Outlet /></main>
      <Footer />
      {!hideSticky && (
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 p-3 bg-[var(--kc-bg)]/90 backdrop-blur-md border-t border-[#E5E2DA]" data-testid="mobile-sticky-cta">
          <Link to="/book" className="btn-primary w-full" data-testid="mobile-sticky-book-now" onClick={() => track("homepage_book_clicked", { source: "mobile-sticky" })}>BOOK NOW</Link>
        </div>
      )}
    </div>
  );
}
